import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { clsx } from '../../../ds/clsx';
import { useFieldIds } from '../../../ds/hooks';
import styles from './controls.module.css';

export type WizardButtonVariant = 'primary' | 'secondary' | 'ghost' | 'dark';

export interface WizardButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary: accent 11/20 · secondary: outline 11/16 · ghost: text only · dark: outline on navy. */
  variant?: WizardButtonVariant | undefined;
  /** small 9/14 · 12.5 px (buttons inside cards), large 13/18 · 14 px (Admin 01), mobile 14/16 (phone frames). */
  size?: 'default' | 'small' | 'large' | 'mobile' | undefined;
  block?: boolean | undefined;
  loading?: boolean | undefined;
}

/** The buttons of the onboarding frames (A2 „Primär-/Sekundär-/Ghost-Button"). */
export function WizardButton({
  variant = 'secondary',
  size = 'default',
  block = false,
  loading = false,
  className,
  type = 'button',
  children,
  onClick,
  ...rest
}: WizardButtonProps) {
  return (
    <button
      type={type}
      className={clsx(
        'pressable',
        styles.button,
        styles[variant],
        size !== 'default' && styles[size],
        block && styles.block,
        className,
      )}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={(event) => {
        if (loading) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...rest}
    >
      {loading ? <span aria-hidden="true" className={styles.spinner} /> : null}
      {children}
    </button>
  );
}

export interface WizardFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  /** Error text below the field (red border, red label). */
  error?: ReactNode | undefined;
  /** Red border and label without a message (the message lives elsewhere). */
  invalid?: boolean | undefined;
  /** Muted text below the field when there is no error. */
  hint?: ReactNode | undefined;
  /** Control at the right end of the field („anzeigen"). */
  trailing?: ReactNode | undefined;
  numeric?: boolean | undefined;
  /** Einstellungen 20: 10/12 padding, 13.5 px text. */
  dense?: boolean | undefined;
  className?: string | undefined;
}

/** Eyebrow label (10.5 px, uppercase) + 14 px input of the onboarding frames. */
export const WizardField = forwardRef<HTMLInputElement, WizardFieldProps>(function WizardField(
  { label, error, invalid, hint, trailing, numeric, dense, className, id, readOnly, ...rest },
  ref,
) {
  const hasError = Boolean(error) || Boolean(invalid);
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  return (
    <div className={clsx(styles.field, className)}>
      <label htmlFor={ids.inputId} className={clsx(styles.label, hasError && styles.labelInvalid)}>
        {label}
      </label>
      <div
        className={clsx(
          styles.control,
          'field-focus',
          dense && styles.controlDense,
          hasError && styles.controlInvalid,
          readOnly && styles.controlReadOnly,
        )}
      >
        <input
          ref={ref}
          id={ids.inputId}
          className={clsx(styles.input, dense && styles.inputDense, numeric && styles.numeric)}
          aria-invalid={hasError || undefined}
          aria-describedby={ids.describedBy}
          readOnly={readOnly}
          {...rest}
        />
        {trailing ? <span className={styles.trailing}>{trailing}</span> : null}
      </div>
      {error ? (
        <div id={ids.errorId} className={clsx(styles.message, styles.messageError)} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={ids.hintId} className={styles.message}>
          {hint}
        </div>
      ) : null}
    </div>
  );
});

export interface WizardSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  /** Hides the label visually (table cells with a column head). */
  labelHidden?: boolean | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  error?: ReactNode | undefined;
  invalid?: boolean | undefined;
  className?: string | undefined;
  controlClassName?: string | undefined;
}

/** Native select in the field frame of the onboarding screens, with the frame's „⌄". */
export function WizardSelect({
  label,
  labelHidden = false,
  options,
  error,
  invalid,
  className,
  controlClassName,
  id,
  ...rest
}: WizardSelectProps) {
  const hasError = Boolean(error) || Boolean(invalid);
  const ids = useFieldIds(id, { error: Boolean(error) });
  return (
    <div className={clsx(styles.field, className)}>
      <label
        htmlFor={ids.inputId}
        className={clsx(styles.label, hasError && styles.labelInvalid, labelHidden && styles.srOnly)}
      >
        {label}
      </label>
      <div
        className={clsx(
          styles.control,
          styles.selectControl,
          'field-focus',
          hasError && styles.controlInvalid,
          controlClassName,
        )}
      >
        <select
          id={ids.inputId}
          className={styles.select}
          aria-invalid={hasError || undefined}
          aria-describedby={ids.describedBy}
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className={styles.chevron} aria-hidden="true">
          ⌄
        </span>
      </div>
      {error ? (
        <div id={ids.errorId} className={clsx(styles.message, styles.messageError)} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
